// Mirayama films: silent looping films that play only while on screen.
// Off-screen, hidden tab or prefers-reduced-motion: the film stays paused on its poster.
// A film with preload="none" downloads only when it first comes on screen.
// Each film gets a Pause / Play button (WCAG 2.2.2), placed next to the <mm-film> (not inside it,
// as product films carry role="img"); its parent is positioned, so the button sits on the film.
if (!customElements.get('mm-film')) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const films = new Set();
  const updateAll = () => films.forEach((film) => film.update());

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.target.inView = entry.isIntersecting;
              entry.target.update();
            });
          },
          { threshold: 0.1 }
        )
      : null;

  document.addEventListener('visibilitychange', updateAll);
  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', updateAll);
  } else if (reduceMotion.addListener) {
    reduceMotion.addListener(updateAll);
  }

  customElements.define(
    'mm-film',
    class MmFilm extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.video.muted = true;
        this.video.playsInline = true;
        this.inView = !observer;
        this.userPaused = false;
        if (!this.toggle && !reduceMotion.matches && this.parentElement) {
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'mm-film__toggle';
          toggle.textContent = 'Pause';
          toggle.setAttribute('aria-label', 'Pause the film');
          toggle.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            toggle.textContent = this.userPaused ? 'Play' : 'Pause';
            toggle.setAttribute('aria-label', this.userPaused ? 'Play the film' : 'Pause the film');
            this.update();
          });
          this.after(toggle);
          this.toggle = toggle;
        }
        films.add(this);
        if (observer) {
          observer.observe(this);
        } else {
          this.update();
        }
      }

      disconnectedCallback() {
        films.delete(this);
        if (observer) observer.unobserve(this);
      }

      update() {
        const video = this.video;
        if (!video) return;
        const shouldPlay = this.inView && !reduceMotion.matches && !document.hidden && !this.userPaused;
        if (shouldPlay && video.paused) {
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        } else if (!shouldPlay && !video.paused) {
          video.pause();
        }
      }
    }
  );
}

// Mirayama curated product gallery: keeps the mobile "01 / 05" counter in step with the scroll-snap row.
if (!customElements.get('mm-gallery')) {
  customElements.define(
    'mm-gallery',
    class MmGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.mm-gallery__track');
        this.current = this.querySelector('.mm-gallery__current');
        if (!this.track || !this.current) return;
        this.items = Array.from(this.track.children);
        this.ticking = false;
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      }

      update() {
        const start = this.track.getBoundingClientRect().left;
        let index = 0;
        let best = Infinity;
        this.items.forEach((item, i) => {
          const distance = Math.abs(item.getBoundingClientRect().left - start);
          if (distance < best) {
            best = distance;
            index = i;
          }
        });
        const label = String(index + 1).padStart(2, '0');
        if (this.current.textContent !== label) this.current.textContent = label;
      }
    }
  );
}
